import useSWR from "swr";
import { useOnlineStock } from "../useOnlineStock/useOnlineStock";

export type UseShowStoreStockButtonProps = {
  displayCode: string;
  isFutureRelease: boolean;
  isPreOrderable: boolean;
  skuId: string;
  stockApiBaseUrl: string;
  stockApiKey: string;
  storeStockApiKey: string;
  storeStockModalApiUrl: string;
  hideWhenStoreStockUnavailable?: boolean;
};

export const ONLINE_STOCK_AVAILABILITY_STATUS_SPECIAL_ORDER =
  "SKU_AVAILABLE_FOR_SPECIAL_ORDER";

export type Status = "isLoading" | "visible" | "disabled" | "hidden";

type StoreModalResponseEntry = {
  productCode: string;
  displayModal: boolean;
};
type StoreStockModalResult = { storeModalResponse: StoreModalResponseEntry[] };

export const useShowStoreStockButton = ({
  displayCode,
  isFutureRelease,
  isPreOrderable,
  skuId,
  stockApiBaseUrl,
  stockApiKey,
  storeStockModalApiUrl,
  storeStockApiKey,
  hideWhenStoreStockUnavailable = false,
}: UseShowStoreStockButtonProps): Status => {
  const onlineStock = useOnlineStock(skuId, stockApiBaseUrl, stockApiKey);
  const storeStockInfoAvailability = useSWR(
    displayCode,
    () =>
      getStoreStockInfoAvailability(
        displayCode,
        storeStockModalApiUrl,
        stockApiBaseUrl,
        storeStockApiKey,
      ),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  if (isFutureRelease || isPreOrderable) {
    return "disabled";
  }

  if (onlineStock.isLoading || storeStockInfoAvailability.isLoading) {
    return "isLoading";
  }

  if (
    onlineStock.data?.availabilityStatus ===
    ONLINE_STOCK_AVAILABILITY_STATUS_SPECIAL_ORDER
  ) {
    return "disabled";
  }

  if (storeStockInfoAvailability.data && !storeStockInfoAvailability.error) {
    if (storeStockInfoAvailability.data?.[displayCode]) {
      return "visible";
    }
    return hideWhenStoreStockUnavailable ? "hidden" : "disabled";
  }

  return "visible";
};

const getStoreStockInfoAvailability = async (
  displayCode: string,
  storeStockModalApiUrl: string,
  stockApiBaseUrl: string,
  stockApiKey: string,
): Promise<Record<string, boolean>> => {
  const requestUrl = new URL(storeStockModalApiUrl, stockApiBaseUrl);
  requestUrl.searchParams.set("productCodes", displayCode);
  requestUrl.searchParams.set("key", stockApiKey);

  return await fetch(requestUrl, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then((response) => {
      if (response.ok) {
        return response.json();
      }
      throw new Error(response.statusText);
    })
    .then((result: StoreStockModalResult) => {
      return result.storeModalResponse.reduce(
        (list, { productCode, displayModal }) => ({
          ...list,
          [productCode]: displayModal,
        }),
        {},
      );
    });
};
